<template>
  <div class="h-full w-full flex flex-col justify-between">
    <div class="w-full max-w-300 mx-auto md:max-w-full md:pl-100 pt-35 md:pt-67" :class="isDarkMode ? 'text-app-color' : 'text-dark-300'">
      <div class="flex items-center md:hidden text-app-color" @click="back">
        <i class="text-12 mr-10 icon-keyboard-arrow-left opacity-50" />
        <p class="text-opacity-80">Back</p>
      </div>

      <h2 class="text-app-color mb-15 font-bold text-35 md:text-21">Account</h2>

      <div class="md:flex items-center mb-12">
        <p class="w-100 mr-30 mb-5 md:mb-0">First name:</p>
        <InputModule
          v-model="localUser.profile.firstName"
          class="w-full md:w-240"
          input-classes="pl-15 py-10"
          @keypress.enter="update"
          @blur="update"
        />
      </div>

      <div class="md:flex items-center mb-12">
        <p class="w-100 mr-30 mb-5 md:mb-0">Last name:</p>
        <InputModule
          v-model="localUser.profile.lastName"
          class="w-full md:w-240"
          input-classes="pl-15 py-10"
          @keypress.enter="update"
          @blur="update"
        />
      </div>

      <div class="md:flex items-center mb-12">
        <p class="w-100 mr-30 mb-5 md:mb-0">Email:</p>
        <div>
          <InputModule
            v-model="localUser.email"
            class="w-full md:w-240"
            input-classes="pl-15 py-10"
            @clearErrorMessage="emailError = null"
            @keypress.enter="update"
            @blur="update"
          />
          <p v-if="emailError" class="text-error text-12">
            {{ emailError }}
          </p>
        </div>
      </div>

      <div class="md:flex items-center mb-12">
        <p class="w-100 mr-30 mb-5 md:mb-0">Password:</p>
        <div>
          <div v-if="isChangePassword && user.isPassword" class="w-full md:w-240 mb-12">
            <InputModule
              v-model="oldPassword"
              type="password"
              placeholder="Old password"
              input-classes="pl-15 py-10 tracking-wide"
            />
          </div>
          <div class="w-full md:w-240 relative">
            <InputModule
              v-model="newPassword"
              type="password"
              :readonly="!isChangePassword"
              :placeholder="isChangePassword ? 'New password' : '**********'"
              input-classes="pl-15 py-10 tracking-wide"
            />
            <p
              class="text-10 text-main-color absolute right-0 top-1/2 change mr-10 cursor-pointer"
              @click="changePassword"
            >
              {{ buttonTitle }}
            </p>
          </div>
        </div>
      </div>

      <div class="md:flex items-center mb-12">
        <p class="w-100 mr-30 mb-5 md:mb-0">Domain:</p>
        <div class="w-full md:w-240 flex items-center">
          <p class="mr-10">helloivy.co/</p>
          <InputModule
            class="w-full"
            :readonly="true"
            :placeholder="user.profile.userName"
            input-classes="pl-15 py-10"
          />
        </div>
      </div>

      <div v-if="isTeamAccount && isOwner" class="md:flex items-center mb-12">
        <p class="w-100 mr-30 mb-5 md:mb-0">Logo:</p>
        <div class="w-full md:w-240 flex items-center">
          <input ref="inputRef" type="file" class="hidden" @change="uploadLogo">
          <ButtonModule
            text="Upload logo"
            class="text-15 rounded-4 px-12 py-9 md:py-12 md:px-15 justify-center border-2"
            :class="isDarkMode ? 'border-hover hover:text-white' : 'border-grey-700 bg-grey-200 text-grey-950 hover:text-grey-main'"
            @click="toggleUploadInput"
          />
        </div>
      </div>
    </div>

    <div class="text-right border-hover">
      <ButtonModule
        text="Delete account"
        :has-hover="true"
        class="text-14 py-15 text-error mr-20 mb-10"
        @click="deleteAccount"
      />
    </div>

    <DeleteAccount ref="confirm" />
  </div>
</template>

<script>
import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import InputModule from '@/components/shared/form-controls/InputModule'
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex'
import { authService, profileService, teamsService } from '@/services'
import DeleteAccount from '@/components/confirm/DeleteAccount'
import { emailValidation } from '@/helpers'
import localStorageService from '@/core/local-storage'

export default {
  components: { ButtonModule, InputModule, DeleteAccount },

  emits: ['back'],

  data () {
    return {
      isChangePassword: false,
      oldPassword: '',
      newPassword: '',
      localUser: {},
      emailError: null
    }
  },

  computed: {
    ...mapState({
      user: (state) => state.profile.user
    }),

    ...mapGetters(['isOwner', 'isTeamAccount', 'isDarkMode']),

    buttonTitle () {
      return this.isChangePassword ? this.newPassword.trim() !== '' ? 'confirm' : 'cancel' : 'change'
    }
  },

  created () {
    if (this.user) this.localUser = JSON.parse(JSON.stringify(this.user))
  },

  unmounted () {
    this.isChangePassword = false
  },

  methods: {
    ...mapActions(['getProfile']),

    ...mapMutations([
      'setNotification',
      'toggleSettingsModal',
      'setUser',
      'setLogo'
    ]),

    deleteAccount () {
      this.$refs.confirm.open()
    },

    changePassword () {
      if (this.isChangePassword && this.newPassword.trim() !== '') {
        authService
          .passwordUpdate({
            oldPassword: this.oldPassword,
            newPassword: this.newPassword
          })
          .then((res) => {
            if (res.data.success) {
              this.toggleSettingsModal(false)
              this.setNotification({
                isVisible: true,
                message: 'Your password was successfully changed!',
                color: 'success'
              })
            }
          })
          .catch(({ data }) => {
            this.toggleSettingsModal(false)
            this.setNotification({
              isVisible: true,
              message: data.error.message,
              color: 'error'
            })
          })
      }

      this.oldPassword = ''
      this.newPassword = ''

      this.isChangePassword = !this.isChangePassword
    },

    logout () {
      this.$router.push({ name: 'Login' })
      this.toggleSettingsModal(false)
      localStorageService.removeItem('lastOpenedProject')
    },

    back () {
      this.$emit('back')
    },

    update () {
      const email = this.localUser.email.trim()
      const firstName = this.localUser.profile.firstName.trim()
      const lastName = this.localUser.profile.lastName.trim()

      const notEmpty = firstName !== '' &&
                       lastName !== '' &&
                          email !== ''

      const needUpdate = firstName !== this.user.profile.firstName ||
                         lastName !== this.user.profile.lastName ||
                            email !== this.user.email

      if (notEmpty && needUpdate) {
        this.emailError = emailValidation(email)

        if (!this.emailError) {
          profileService
            .update({
              email: email,
              firstName: firstName,
              lastName: lastName
            })
            .then(() => this.getProfile())
        }
      } else {
        this.emailError = null
        this.setUser(JSON.parse(JSON.stringify(this.user)))
      }
    },

    toggleUploadInput () {
      this.$refs.inputRef.click()
    },

    uploadLogo (event) {
      const hasFiles = event.target.files.length

      if (!hasFiles) return

      const files = Array
        .from(event.target.files)
        .filter(file => (/image/i).test(file.type))

      if (files.length === 0) return

      const formData = new FormData()
      formData.append('data', files[0])

      return teamsService.upload(formData)
        .then(res => {
          this.setLogo(res.data)
        })
    }
  }
}
</script>

<style scoped>
.change {
  transform: translateY(-50%);
}
</style>
